// FORGED Ed Systems — DEPLOY002 — v0.2.0 — RegisterPage.tsx
// Last touched: 2026-05-19
//
// Registration form matching form mockup §1. Submits to /api/register-account,
// then immediately signs the user in via Supabase Auth and redirects to /dashboard.

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { registerAccount } from "./api";
import { supabase } from "./supabaseClient";

type FormState = {
  full_name: string;
  email: string;
  school_name: string;
  school_district: string;
  role: "instructor" | "program_director" | "cte_coordinator" | "other";
  heard_about_us_from: string;
  pilot_code: string;
  consent_marketing_email: boolean;
  consent_share_with_forgedweldscan: boolean;
  password: string;
};

const INITIAL_STATE: FormState = {
  full_name: "",
  email: "",
  school_name: "",
  school_district: "",
  role: "instructor",
  heard_about_us_from: "",
  pilot_code: "",
  consent_marketing_email: false,
  consent_share_with_forgedweldscan: false,
  password: "",
};

export default function RegisterPage() {
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    // Quick client-side validation. Server validates again.
    if (form.password.length < 12) {
      setErrorMsg("Please use at least 12 characters for your password.");
      return;
    }

    setSubmitting(true);
    const result = await registerAccount({
      full_name: form.full_name,
      email: form.email,
      school_name: form.school_name,
      school_district: form.school_district || null,
      role: form.role,
      heard_about_us_from: form.heard_about_us_from || null,
      pilot_code: form.pilot_code || null,
      consent_marketing_email: form.consent_marketing_email,
      consent_share_with_forgedweldscan: form.consent_share_with_forgedweldscan,
      password: form.password,
    });

    if (!result.ok) {
      setSubmitting(false);
      setErrorMsg(result.message);
      return;
    }

    // Registration succeeded. Sign in immediately using the same credentials.
    const signInRes = await supabase.auth.signInWithPassword({
      email: form.email,
      password: form.password,
    });

    setSubmitting(false);

    if (signInRes.error) {
      setErrorMsg(
        "Your account was created but sign-in failed: " +
          signInRes.error.message +
          ". Try signing in manually."
      );
      navigate("/sign-in");
      return;
    }

    navigate("/dashboard");
  }

  return (
    <main className="container">
      <header className="brand-header">
        <h1>forgedsight</h1>
        <p className="tagline">Free strategic planning for your welding program.</p>
      </header>

      <p style={{ marginBottom: "1.5rem", color: "var(--color-text-muted)" }}>
        Sign up to start. We'll never share your data with curriculum vendors. You'll
        keep your plans in your dashboard.
      </p>

      <form onSubmit={handleSubmit} className="form-stack">
        <label className="field">
          <span>Full name</span>
          <input
            type="text"
            required
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            autoComplete="name"
          />
        </label>

        <label className="field">
          <span>Work email</span>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            autoComplete="email"
          />
        </label>

        <label className="field">
          <span>School name</span>
          <input
            type="text"
            required
            value={form.school_name}
            onChange={(e) => update("school_name", e.target.value)}
            autoComplete="organization"
          />
        </label>

        <label className="field">
          <span>School district (optional)</span>
          <input
            type="text"
            value={form.school_district}
            onChange={(e) => update("school_district", e.target.value)}
          />
        </label>

        <label className="field">
          <span>Your role</span>
          <select
            value={form.role}
            onChange={(e) => update("role", e.target.value as FormState["role"])}
          >
            <option value="instructor">Instructor</option>
            <option value="program_director">Program Director</option>
            <option value="cte_coordinator">CTE Coordinator</option>
            <option value="other">Other</option>
          </select>
        </label>

        <label className="field">
          <span>State</span>
          <select value="TX" disabled>
            <option value="TX">Texas</option>
          </select>
          <small className="field-hint">
            forgedsight is Texas-only at launch. More states coming.
          </small>
        </label>

        <label className="field">
          <span>How did you hear about us? (optional)</span>
          <input
            type="text"
            value={form.heard_about_us_from}
            onChange={(e) => update("heard_about_us_from", e.target.value)}
          />
        </label>

        <label className="field">
          <span>Pilot access code (optional)</span>
          <input
            type="text"
            value={form.pilot_code}
            onChange={(e) => update("pilot_code", e.target.value)}
            placeholder="Enter your pilot code if you have one"
          />
          <small className="field-hint">
            Pilot participants receive an additional curriculum evaluation option. Most
            teachers leave this blank.
          </small>
        </label>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.consent_marketing_email}
            onChange={(e) => update("consent_marketing_email", e.target.checked)}
          />
          <span>OK to email me product updates.</span>
        </label>

        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={form.consent_share_with_forgedweldscan}
            onChange={(e) =>
              update("consent_share_with_forgedweldscan", e.target.checked)
            }
          />
          <span>
            OK to contact me about forgedweldscan — the paid plate-level evaluation
            platform.
          </span>
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            required
            value={form.password}
            onChange={(e) => update("password", e.target.value)}
            autoComplete="new-password"
            minLength={12}
          />
          <small className="field-hint">Minimum 12 characters.</small>
        </label>

        {errorMsg && <div className="error-banner">{errorMsg}</div>}

        <button type="submit" className="cta primary" disabled={submitting}>
          {submitting ? "Creating your account…" : "Create my account"}
        </button>

        <p style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
          By creating an account, you agree to our Terms of Service and Privacy
          Policy.
        </p>

        <p style={{ color: "var(--color-text-muted)" }}>
          Already have an account? <Link to="/sign-in">Sign in</Link>
        </p>
      </form>
    </main>
  );
}
